import { useMemo, useState } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useGetCoaches } from 'actions/coaches';
import { useParams } from 'react-router-dom';
import { Combobox, ComboboxOption } from '@teachstonellc/component-library';

interface Coach {
  id: number;
  user_id?: number;
  name?: string;
  email: string;
}

interface CoachAutocompleteProps {
  value: string;
  handleChange: ({ email, id }: Coach) => void;
  placeholder?: string;
  onSelect?: (customer: Coach) => void;
}

function CoachAutocomplete(props: CoachAutocompleteProps) {
  const {
    value,
    handleChange,
    placeholder = i18n.ft(messages.placeholder),
    onSelect
  } = props;
  const [query, setQuery] = useState('');
  const { id: subscriptionId } = useParams();

  const { data, isLoading } = useGetCoaches(Number(subscriptionId), 'lr_coach');

  const coaches = data?.subscription_users ?? [];

  const coachOptions = useMemo<ComboboxOption[]>(() => {
    return coaches.map((coach: Coach) => ({
      label: coach.name ? `${coach.email} (${coach.name})` : coach.email,
      value: coach.id
    }));
  }, [coaches]);

  const selectedCoach = useMemo<Coach | undefined>(() => {
    return coaches.find((coach: Coach) => coach.email === value);
  }, [coaches, value]);

  const selectedOption = useMemo<ComboboxOption | null>(() => {
    if (!value) return null;

    if (selectedCoach) {
      return {
        label: selectedCoach.name
          ? `${selectedCoach.email} (${selectedCoach.name})`
          : selectedCoach.email,
        value: selectedCoach.id
      };
    }

    return {
      label: value,
      value
    };
  }, [selectedCoach, value]);

  const optionsWithSelected = useMemo<ComboboxOption[]>(() => {
    if (!selectedOption) return coachOptions;

    const hasSelected = coachOptions.some(
      option => String(option.value) === String(selectedOption.value)
    );

    return hasSelected ? coachOptions : [selectedOption, ...coachOptions];
  }, [coachOptions, selectedOption]);

  const handleComboboxChange = (option: ComboboxOption | null) => {
    if (!option) {
      handleChange({ email: '', id: 0 });
      onSelect?.({ email: '', id: 0 });
      return;
    }

    const selected = coaches.find(
      (coach: Coach) => String(coach.id) === String(option.value)
    );

    if (selected) {
      handleChange(selected);
      onSelect?.(selected);
      return;
    }

    handleChange({ email: String(option.label), id: 0 });
  };

  const handleQueryChange = (nextQuery: string) => {
    setQuery(nextQuery);
  };

  return (
    <Combobox
      label={i18n.ft(messages.coach)}
      options={optionsWithSelected}
      selectedOption={selectedOption}
      onChange={handleComboboxChange}
      placeholder={placeholder}
      query={query}
      setQuery={handleQueryChange}
      showSearchIcon
      disabled={isLoading}
    />
  );
}

export default CoachAutocomplete;
